<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, nextTick, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import { MonthYear } from '@common/types'

    import { useStartedProjectsPageQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useSaveViewStartedProjectsPageCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import { useGridProjectByIdQueryV1 } from '@Quest/Projects/queries-vue-composables'

    import
        {
            useSetLifecyclePausedCommandV1,
            useSetLifecycleResumedCommandV1,
            useSetLifecycleTerminatedCommandV1,
            useSetLifecycleCompletedCommandV1
        } from '@Quest/Projects/commands-vue-composables'

    import { StartedProjectsPageReadModel } from '@Quest/Portfolios/readmodels'
    import { GridProjectReadModel } from '@Quest/Projects/readmodels'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectPausedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectPausedLifecycleSetter'
    import ProjectPausedLifecycleSetter from '@Quest/Projects/Components/ProjectPausedLifecycleSetter.vue'

    import { ProjectResumedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectResumedLifecycleSetter'
    import ProjectResumedLifecycleSetter from '@Quest/Projects/Components/ProjectResumedLifecycleSetter.vue'

    import { ProjectTerminatedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectTerminatedLifecycleSetter'
    import ProjectTerminatedLifecycleSetter from '@Quest/Projects/Components/ProjectTerminatedLifecycleSetter.vue'

    import { ProjectCompletedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectCompletedLifecycleSetter'
    import ProjectCompletedLifecycleSetter from '@Quest/Projects/Components/ProjectCompletedLifecycleSetter.vue'



    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))



    // ======================
    // Query
    // ======================

    const {
        runStartedProjectsPageQueryV1,
        modelStartedProjectsPageQueryV1
    } = useStartedProjectsPageQueryV1(workspaceId, tchProjectId)

    const rm = modelStartedProjectsPageQueryV1 as Ref<StartedProjectsPageReadModel | null>

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const portfolioName = computed(() => rm.value?.PortfolioName ?? null)



    // ======================
    // Success ID caches
    // ======================

    const lastPausedId = ref<string | null>(null)
    const lastResumedId = ref<string | null>(null)
    const lastTerminatedId = ref<string | null>(null)
    const lastCompletedId = ref<string | null>(null)



    // ======================
    // Grid project query
    // ======================

    const {
        runGridProjectByIdQueryV1,
        modelGridProjectByIdQueryV1
    } = useGridProjectByIdQueryV1(workspaceId, portfolioId)



    // ======================
    // Commands
    // ======================

    const {
        invokeSaveViewStartedProjectsPageCommandV1,
        succeedSaveViewStartedProjectsPageCommandV1
    } = useSaveViewStartedProjectsPageCommandV1(workspaceId, portfolioId)

    const {
        invokeSetLifecyclePausedCommandV1,
        succeedSetLifecyclePausedCommandV1
    } = useSetLifecyclePausedCommandV1(workspaceId, portfolioId, lastPausedId)

    const {
        invokeSetLifecycleResumedCommandV1,
        succeedSetLifecycleResumedCommandV1
    } = useSetLifecycleResumedCommandV1(workspaceId, portfolioId, lastResumedId)

    const {
        invokeSetLifecycleTerminatedCommandV1,
        succeedSetLifecycleTerminatedCommandV1
    } = useSetLifecycleTerminatedCommandV1(workspaceId, portfolioId, lastTerminatedId)

    const {
        invokeSetLifecycleCompletedCommandV1,
        succeedSetLifecycleCompletedCommandV1
    } = useSetLifecycleCompletedCommandV1(workspaceId, portfolioId, lastCompletedId)



    // ======================
    // ViewModels
    // ======================

    const grid = shallowReactive(new ProjectsGridViewModel())

    const projectPausedLifecycleSetter = shallowReactive(new ProjectPausedLifecycleSetterViewModel())
    const projectResumedLifecycleSetter = shallowReactive(new ProjectResumedLifecycleSetterViewModel())
    const projectTerminatedLifecycleSetter = shallowReactive(new ProjectTerminatedLifecycleSetterViewModel())
    const projectCompletedLifecycleSetter = shallowReactive(new ProjectCompletedLifecycleSetterViewModel())



    // ======================
    // Handlers
    // ======================

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        invokeSaveViewStartedProjectsPageCommandV1({
            Grid: grid.getJson()
        })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function handlePaused(projectId: string)
    {
        lastPausedId.value = projectId
        invokeSetLifecyclePausedCommandV1()
    }

    function handleResumed(projectId: string)
    {
        lastResumedId.value = projectId
        invokeSetLifecycleResumedCommandV1()
    }

    function handleTerminated(projectId: string)
    {
        lastTerminatedId.value = projectId
        invokeSetLifecycleTerminatedCommandV1()
    }

    function handleCompleted(projectId: string)
    {
        lastCompletedId.value = projectId
        invokeSetLifecycleCompletedCommandV1()
    }



    // ======================
    // Query Watchers
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        nextTick(() =>
        {
            grid.renderGrid(
                current.Grid.IdField,
                current.Grid.Fields,
                current.Grid.DefaultFieldIds,
                current.Grid.Options
            )
            grid.setProjects(current.Projects.map(p => p.Properties))
        })
    })

    watch(modelGridProjectByIdQueryV1 as Ref<GridProjectReadModel | null>, (current) =>
    {
        if (!current || !rm.value) return

        rm.value.Projects = rm.value.Projects.filter(p => p.Id !== current.Id)
        rm.value.Projects.push(current)

        grid.update(current.Properties)
    })



    // ======================
    // Command Watchers
    // ======================

    watch(succeedSaveViewStartedProjectsPageCommandV1, (c, p) =>
    {
        if (c && !p) alert('Saved')
    })

    watch(succeedSetLifecyclePausedCommandV1, (c, p) =>
    {
        if (c && !p && lastPausedId.value)
        {
            runGridProjectByIdQueryV1({ ProjectId: lastPausedId.value })
            projectPausedLifecycleSetter.close()
            lastPausedId.value = null
        }
    })

    watch(succeedSetLifecycleResumedCommandV1, (c, p) =>
    {
        if (c && !p && lastResumedId.value)
        {
            runGridProjectByIdQueryV1({ ProjectId: lastResumedId.value })
            projectResumedLifecycleSetter.close()
            lastResumedId.value = null
        }
    })

    watch(succeedSetLifecycleTerminatedCommandV1, (c, p) =>
    {
        if (c && !p && lastTerminatedId.value && rm.value)
        {
            const id = lastTerminatedId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectTerminatedLifecycleSetter.close()
            lastTerminatedId.value = null
        }
    })

    watch(succeedSetLifecycleCompletedCommandV1, (c, p) =>
    {
        if (c && !p && lastCompletedId.value && rm.value)
        {
            const id = lastCompletedId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectCompletedLifecycleSetter.close()
            lastCompletedId.value = null
        }
    })

    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Started | TransparentChoice"
        runStartedProjectsPageQueryV1()
    })
</script>

<template>
    <div>
        <ProjectPausedLifecycleSetter :viewModel="projectPausedLifecycleSetter" @confirm="handlePaused" />
        <ProjectResumedLifecycleSetter :viewModel="projectResumedLifecycleSetter" @confirm="handleResumed" />
        <ProjectTerminatedLifecycleSetter :viewModel="projectTerminatedLifecycleSetter" @confirm="handleTerminated" />
        <ProjectCompletedLifecycleSetter :viewModel="projectCompletedLifecycleSetter" @confirm="handleCompleted" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6 class="text-muted mt-2">{{portfolioName}}</h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Started projects</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleSaveViewSettings">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="toolbar">
                <div>
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div>
                    <button type="button" class="btn btn-outline-secondary mr-1" @click="handleShowGridColumnsEditor">Edit columns</button>

                    <div class="btn-group mr-1">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" @click="handleClearGridFilters">Clear Filters</a>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-secondary mr-1" @click="handleExportGrid" title="Download to spreadsheet">
                        <i class="fa fa-download fa-lg"></i>
                    </button>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />

        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>